<template>
  <div
    v-if="requestHistories"
    class="request-histories-page"
  >
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-icon-wrapper">
            <feather-icon
              icon="FileTextIcon"
              size="28"
            />
          </div>
          <div class="header-text">
            <h1 class="page-title">
              Request Information
            </h1>
            <p
              v-if="requestHistories.request_code"
              class="page-subtitle"
            >
              {{ requestHistories.request_code }}
            </p>
          </div>
        </div>
        <b-button
          v-if="requestHistories.facility"
          variant="primary"
          size="sm"
          class="btn-edit-facility"
          @click="openEditFacilityModal"
        >
          <feather-icon
            icon="Edit3Icon"
            size="16"
          />
          <span>Edit Facility</span>
        </b-button>
      </div>
    </div>

    <!-- Quick Stats -->
    <b-row class="stats-row">
      <b-col
        cols="6"
        md="3"
      >
        <div class="stat-card">
          <div class="stat-icon stat-icon-primary">
            <feather-icon
              icon="TagIcon"
              size="20"
            />
          </div>
          <div class="stat-content">
            <span class="stat-label">Request Code</span>
            <span class="stat-value">{{ requestHistories.request_code || '—' }}</span>
          </div>
        </div>
      </b-col>
      <b-col
        cols="6"
        md="3"
      >
        <div class="stat-card">
          <div class="stat-icon stat-icon-success">
            <feather-icon
              icon="CreditCardIcon"
              size="20"
            />
          </div>
          <div class="stat-content">
            <span class="stat-label">Payment Status</span>
            <span class="stat-value">{{ requestHistories.payment_status || '—' }}</span>
          </div>
        </div>
      </b-col>
      <b-col
        cols="6"
        md="3"
      >
        <div class="stat-card">
          <div class="stat-icon stat-icon-warning">
            <feather-icon
              icon="CalendarIcon"
              size="20"
            />
          </div>
          <div class="stat-content">
            <span class="stat-label">Due Date</span>
            <span class="stat-value">{{ requestHistories.due_date || '—' }}</span>
          </div>
        </div>
      </b-col>
      <b-col
        cols="6"
        md="3"
      >
        <div class="stat-card">
          <div class="stat-icon stat-icon-info">
            <feather-icon
              icon="GlobeIcon"
              size="20"
            />
          </div>
          <div class="stat-content">
            <span class="stat-label">Source</span>
            <span class="stat-value">{{ requestHistories.source || '—' }}</span>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Content Sections -->
    <b-row>
      <!-- Certificate Section -->
      <b-col
        v-if="requestHistories.certificate"
        md="6"
        lg="4"
        class="mb-1"
      >
        <div class="section-card">
          <div class="section-header">
            <div class="section-icon section-icon-certificate">
              <feather-icon
                icon="AwardIcon"
                size="22"
              />
            </div>
            <h3 class="section-title">
              Certificate
            </h3>
          </div>
          <div class="section-body">
            <div
              v-if="requestHistories.certificate.name"
              class="info-row"
            >
              <feather-icon
                icon="FileTextIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Name (AR)</span>
                <span class="info-value">{{ requestHistories.certificate.name.ar }}</span>
              </div>
            </div>
            <div
              v-if="requestHistories.certificate.name"
              class="info-row"
            >
              <feather-icon
                icon="FileTextIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Name (EN)</span>
                <span class="info-value">{{ requestHistories.certificate.name.en }}</span>
              </div>
            </div>
            <div
              v-if="requestHistories.certificate.description"
              class="info-row"
            >
              <feather-icon
                icon="AlignLeftIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Description</span>
                <span class="info-value">{{ requestHistories.certificate.description.ar }} / {{ requestHistories.certificate.description.en }}</span>
              </div>
            </div>
            <div
              v-if="requestHistories.certificate.process_type"
              class="info-row"
            >
              <feather-icon
                icon="SettingsIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Process Type</span>
                <span class="info-value">{{ requestHistories.certificate.process_type }}</span>
              </div>
            </div>
            <div
              v-if="requestHistories.certificate.proccess_time_days"
              class="info-row"
            >
              <feather-icon
                icon="ClockIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Process Time (Days)</span>
                <span class="info-value">{{ requestHistories.certificate.proccess_time_days }}</span>
              </div>
            </div>
            <div
              v-if="requestHistories.certificate.expiratino_duration_months"
              class="info-row"
            >
              <feather-icon
                icon="TrendingUpIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Expiration (Months)</span>
                <span class="info-value">{{ requestHistories.certificate.expiratino_duration_months }}</span>
              </div>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Office & Offer Section -->
      <b-col
        v-if="requestHistories.office && requestHistories.offer"
        md="6"
        lg="4"
        class="mb-1"
      >
        <div class="section-card">
          <div class="section-header">
            <div class="section-icon section-icon-office">
              <feather-icon
                icon="BuildingIcon"
                size="22"
              />
            </div>
            <h3 class="section-title">
              Office & Offer
            </h3>
          </div>
          <div class="section-body">
            <div
              v-if="requestHistories.office.name"
              class="info-row"
            >
              <feather-icon
                icon="HomeIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Office Name</span>
                <span class="info-value">{{ requestHistories.office.name }}</span>
              </div>
            </div>
            <div
              v-if="requestHistories.office.country"
              class="info-row"
            >
              <feather-icon
                icon="MapPinIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Country</span>
                <span class="info-value">{{ requestHistories.office.country }}</span>
              </div>
            </div>
            <div
              v-if="requestHistories.offer.name"
              class="info-row"
            >
              <feather-icon
                icon="PackageIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Offer</span>
                <span class="info-value">{{ requestHistories.offer.name }}</span>
              </div>
            </div>
            <div
              v-if="requestHistories.offer.amount"
              class="info-row"
            >
              <feather-icon
                icon="DollarSignIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Amount</span>
                <span class="info-value">{{ requestHistories.offer.amount }}</span>
              </div>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Facility Section -->
      <b-col
        v-if="requestHistories.facility"
        md="6"
        lg="4"
        class="mb-1"
      >
        <div class="section-card section-card-highlight">
          <div class="section-header">
            <div class="section-icon section-icon-facility">
              <feather-icon
                icon="ArchiveIcon"
                size="22"
              />
            </div>
            <h3 class="section-title">
              Facility
            </h3>
          </div>
          <div class="section-body">
            <div
              v-if="requestHistories.facility.name"
              class="info-row"
            >
              <feather-icon
                icon="BriefcaseIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Name</span>
                <span class="info-value">{{ requestHistories.facility.name.ar }} / {{ requestHistories.facility.name.en }}</span>
              </div>
            </div>
            <div
              v-if="requestHistories.facility.CR_num"
              class="info-row"
            >
              <feather-icon
                icon="HashIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">CR Num</span>
                <span class="info-value">{{ requestHistories.facility.CR_num }}</span>
              </div>
            </div>
            <div
              v-if="requestHistories.facility.phone"
              class="info-row"
            >
              <feather-icon
                icon="PhoneIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Phone</span>
                <span class="info-value">{{ requestHistories.facility.phone }}</span>
              </div>
            </div>
            <div
              v-if="requestHistories.facility.activity"
              class="info-row"
            >
              <feather-icon
                icon="ActivityIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Activity</span>
                <span class="info-value">{{ requestHistories.facility.activity.en }} / {{ requestHistories.facility.activity.ar }}</span>
              </div>
            </div>
            <div
              v-if="requestHistories.facility.address"
              class="info-row"
            >
              <feather-icon
                icon="MapPinIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Address</span>
                <span class="info-value">{{ requestHistories.facility.address }}</span>
              </div>
            </div>
            <div
              v-if="requestHistories.facility.postal_code"
              class="info-row"
            >
              <feather-icon
                icon="MailIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Postal Code</span>
                <span class="info-value">{{ requestHistories.facility.postal_code }}</span>
              </div>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Delegator Section -->
      <b-col
        v-if="requestHistories.delegator"
        md="6"
        lg="4"
        class="mb-1"
      >
        <div class="section-card">
          <div class="section-header">
            <div class="section-icon section-icon-delegator">
              <feather-icon
                icon="UserIcon"
                size="22"
              />
            </div>
            <h3 class="section-title">
              Delegator
            </h3>
          </div>
          <div class="section-body">
            <div
              v-if="requestHistories.delegator.name"
              class="info-row"
            >
              <feather-icon
                icon="UserIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Name</span>
                <span class="info-value">{{ requestHistories.delegator.name }}</span>
              </div>
            </div>
            <div
              v-if="requestHistories.delegator.email"
              class="info-row"
            >
              <feather-icon
                icon="MailIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Email</span>
                <span class="info-value">{{ requestHistories.delegator.email }}</span>
              </div>
            </div>
            <div
              v-if="requestHistories.delegator.phone"
              class="info-row"
            >
              <feather-icon
                icon="PhoneIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Phone</span>
                <span class="info-value">{{ requestHistories.delegator.phone }}</span>
              </div>
            </div>
            <div
              v-if="requestHistories.delegator.wallet_balance != null"
              class="info-row"
            >
              <feather-icon
                icon="DollarSignIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Wallet Balance</span>
                <span class="info-value">{{ requestHistories.delegator.wallet_balance }}</span>
              </div>
            </div>
            <div
              v-if="requestHistories.delegator.offer"
              class="info-row"
            >
              <feather-icon
                icon="TrendingUpIcon"
                size="16"
                class="info-icon"
              />
              <div>
                <span class="info-label">Offer</span>
                <span class="info-value">{{ requestHistories.delegator.offer.name }} — {{ requestHistories.delegator.offer.amount }}%</span>
              </div>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Assigned To Section -->
      <b-col
        v-if="requestHistories.assigned_to && requestHistories.assigned_to.length > 0"
        cols="12"
        class="mb-1"
      >
        <div class="section-card section-card-full">
          <div class="section-header">
            <div class="section-icon section-icon-assigned">
              <feather-icon
                icon="UsersIcon"
                size="22"
              />
            </div>
            <h3 class="section-title">
              Assigned To
            </h3>
          </div>
          <b-row>
            <b-col
              v-for="assigned in requestHistories.assigned_to"
              :key="assigned.id"
              md="6"
              lg="4"
              class="mb-1"
            >
              <div class="assigned-card">
                <div class="assigned-avatar">
                  <feather-icon
                    icon="UserCheckIcon"
                    size="24"
                  />
                </div>
                <div class="assigned-info">
                  <div
                    v-if="assigned.name"
                    class="assigned-name"
                  >
                    {{ assigned.name }}
                  </div>
                  <div
                    v-if="assigned.email"
                    class="assigned-detail"
                  >
                    <feather-icon
                      icon="MailIcon"
                      size="14"
                    />
                    {{ assigned.email }}
                  </div>
                  <div
                    v-if="assigned.phone"
                    class="assigned-detail"
                  >
                    <feather-icon
                      icon="PhoneIcon"
                      size="14"
                    />
                    {{ assigned.phone }}
                  </div>
                  <div
                    v-if="assigned.job"
                    class="assigned-detail"
                  >
                    <feather-icon
                      icon="BriefcaseIcon"
                      size="14"
                    />
                    {{ assigned.job }}
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>

    <!-- Edit Facility Modal -->
    <b-modal
      v-model="editFacilityModalVisible"
      title="Edit Facility"
      centered
      size="lg"
      modal-class="edit-facility-modal"
      header-class="modal-header-custom"
      @show="populateEditForm"
      @hidden="resetEditForm"
    >
      <template #modal-header>
        <div class="modal-header-content">
          <div class="modal-header-icon">
            <feather-icon
              icon="Edit3Icon"
              size="24"
            />
          </div>
          <h5 class="modal-title">
            Edit Facility Details
          </h5>
        </div>
      </template>
      <b-form>
        <b-row>
          <b-col md="6">
            <b-form-group
              label="Name"
              label-for="edit-facility-name"
            >
              <b-form-input
                id="edit-facility-name"
                v-model="editFacilityForm.name"
                trim
                placeholder="Facility Name"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="CR Num"
              label-for="edit-facility-cr-num"
            >
              <b-form-input
                id="edit-facility-cr-num"
                v-model="editFacilityForm.CR_num"
                trim
                placeholder="CR Num"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="Phone"
              label-for="edit-facility-phone"
            >
              <b-form-input
                id="edit-facility-phone"
                v-model="editFacilityForm.phone"
                trim
                placeholder="Phone"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="Activity"
              label-for="edit-facility-activity"
            >
              <b-form-input
                id="edit-facility-activity"
                v-model="editFacilityForm.activity"
                trim
                placeholder="Activity"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="Address"
              label-for="edit-facility-address"
            >
              <b-form-input
                id="edit-facility-address"
                v-model="editFacilityForm.address"
                trim
                placeholder="Address"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="Email"
              label-for="edit-facility-email"
            >
              <b-form-input
                id="edit-facility-email"
                v-model="editFacilityForm.email"
                type="email"
                trim
                placeholder="Email"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="editFacilityModalVisible = false"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="editFacilityLoading"
          @click="submitEditFacility"
        >
          <b-spinner
            v-if="editFacilityLoading"
            small
            type="grow"
            class="mr-50"
          />
          Save Changes
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

export default {
  data() {
    return {
      loadCard: false,
      requestHistories: [],
      editFacilityModalVisible: false,
      editFacilityLoading: false,
      editFacilityForm: {
        id: null,
        name: '',
        CR_num: '',
        CR_end_date: '',
        phone: '',
        activity: '',
        address: '',
        email: '',
      },
    }
  },

  mounted() {
    this.showRequestHistories()
  },
  methods: {
    openEditFacilityModal() {
      this.editFacilityModalVisible = true
    },
    populateEditForm() {
      const { facility } = this.requestHistories || {}
      if (!facility) return
      const nameStr = typeof facility.name === 'string'
        ? facility.name
        : (facility.name?.en || facility.name?.ar || '')
      const activityStr = typeof facility.activity === 'string'
        ? facility.activity
        : (facility.activity?.en || facility.activity?.ar || '')
      this.editFacilityForm = {
        id: facility.id,
        name: nameStr,
        CR_num: facility.CR_num || '',
        CR_end_date: facility.CR_end_date || '',
        phone: facility.phone || '',
        activity: activityStr,
        address: facility.address || '',
        email: facility.email || '',
      }
    },
    resetEditForm() {
      this.editFacilityForm = {
        id: null,
        name: '',
        CR_num: '',
        CR_end_date: '',
        phone: '',
        activity: '',
        address: '',
        email: '',
      }
    },
    submitEditFacility() {
      this.editFacilityLoading = true
      const payload = {
        id: this.editFacilityForm.id,
        name: this.editFacilityForm.name,
        CR_num: this.editFacilityForm.CR_num,
        CR_end_date: this.editFacilityForm.CR_end_date,
        phone: this.editFacilityForm.phone,
        activity: this.editFacilityForm.activity,
        address: this.editFacilityForm.address,
        email: this.editFacilityForm.email,
      }
      axios
        .post('/facility', payload)
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Facility updated successfully',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.editFacilityModalVisible = false
          this.showRequestHistories()
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.response?.data?.message || 'Failed to update facility',
              icon: 'BellIcon',
              variant: 'error',
            },
          })
        })
        .finally(() => {
          this.editFacilityLoading = false
        })
    },
    showRequestHistories() {
      this.loadCard = true
      axios
        .get(`requests/${this.$route.params.id}/short-info`)
        .then(res => {
          this.requestHistories = decryptData(res.data.payload).data.request
        })
        .catch(error => {
          if (error.response?.status) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: `${error.response.data.message}`,
                icon: 'BellIcon',
                variant: 'error',
              },
            })
          }
        })
        .finally(() => {
          this.loadCard = false
        })
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/pages/request/request-histories.scss"></style>
